import { useEffect } from 'react'

import { EMPTY_LIST } from '../../../../constants/common'
import { useDebounceFunc, useInfiniteItemsLoader } from '../../../../hooks'
import { useSelectSearch } from '../useSelectSearch'
import { useSelectShowSelected } from '../useSelectShowSelected'
import { useSelectStates } from '../useSelectStates'

import {
  ASYNC_SELECT_LOAD_ALL_IDWELL_API_PAGE_SIZE_PARAMS,
  ASYNC_SELECT_LOAD_NEXT_THRESHOLD_END_INDEX,
  MIN_SEARCH_SELECT_LENGTH,
} from './useAsyncSelectStates.constants'
import {
  getAsyncSelectSearchApiParam,
  getAsyncSelectShowSelectedApiParam,
} from './useAsyncSelectStates.utils'

export function useAsyncSelectStates({
  apiParams,
  blocked,
  checkIsLastPage,
  clearSearchOnSelect,
  disabled,
  dropdownFloatingConfig,
  getApiParamsLoadNext,
  getData,
  getDataItems,
  getPermissionsData,
  getSearchApiParam = getAsyncSelectSearchApiParam,
  getShowSelectedApiParam = getAsyncSelectShowSelectedApiParam,
  initialItems,
  initialSearchValue,
  loadAllApiParams = ASYNC_SELECT_LOAD_ALL_IDWELL_API_PAGE_SIZE_PARAMS,
  maxLength,
  minSearchLength = MIN_SEARCH_SELECT_LENGTH,
  multi,
  observerOptions,
  thresholdEndIndex = ASYNC_SELECT_LOAD_NEXT_THRESHOLD_END_INDEX,
  toPreparedItems,
  value,
  withPagination = true,
  onChangeSearch,
  onCloseDropdown,
  onSelectAll,
  onSelectItem,
}) {
  const {
    handleChangeSearch: handleChangeSelectSearch,
    handleClearSearch: handleClearSelectSearch,
    searchValue,
  } = useSelectSearch({
    initialValue: initialSearchValue,
    maxLength,
    onChangeSearch,
  })

  const { handleHideSelected, handleToggleShowSelected, showSelected } =
    useSelectShowSelected({
      value,
    })

  const canLoadData = searchValue.length >= minSearchLength

  // #TODO Possibly whould be refactored within ref to update params in callbacks
  const memoizedApiParams = {
    ...apiParams,
    ...getSearchApiParam(searchValue),
    ...getShowSelectedApiParam({ loadAllApiParams, showSelected, value }),
  }

  const {
    getThresholdItemProps,
    items: options,
    loadingInitial,
    loadingMore,
    loadInitial,
    setItems,
    totalCount,
  } = useInfiniteItemsLoader({
    apiParams: memoizedApiParams,
    checkIsLastPage,
    getApiParamsLoadNext,
    getData,
    getDataItems,
    getPermissionsData,
    initialItems,
    observerOptions,
    thresholdEndIndex,
    toPreparedItems,
    withPagination,
  })

  const {
    activeIndex,
    context,
    elementsRef,
    floatingStyles,
    getFloatingItemProps,
    getFloatingProps,
    getReferenceProps,
    handleClose,
    handleKeyDown,
    handleOpen,
    handleSelect,
    handleToggleOpen,
    isOpen,
    refs,
    setOpen,
  } = useSelectStates({
    allowOpen: canLoadData,
    blocked,
    clearSearchOnSelect,
    disabled,
    dropdownFloatingConfig,
    multi,
    options,
    value,
    onClearSearch: handleClearSelectSearch,
    onClose: onCloseDropdown,
    onOpen: loadInitial,
    onSelectItem,
  })

  useEffect(() => {
    if (!isOpen) {
      handleHideSelected()
    }
  }, [isOpen])

  // #TODO Possibly whould be refactored within load new data in callback
  useEffect(() => {
    if (isOpen) {
      loadInitial()
    }
  }, [showSelected])

  const { debouncedHandler } = useDebounceFunc(loadInitial)

  function handleClearSearch() {
    handleClearSelectSearch()

    if (isOpen) {
      if (minSearchLength) {
        setItems(EMPTY_LIST)
        handleClose()
      } else {
        debouncedHandler()
      }
    }
  }

  function handleChangeSearch(value) {
    handleChangeSelectSearch(value)

    if (value.length >= minSearchLength) {
      if (!isOpen) {
        setOpen(true)
      }

      debouncedHandler()
    } else {
      setItems(EMPTY_LIST)
      handleClose()
    }
  }

  function handleSelectAll() {
    loadInitial(loadAllApiParams).then(items => {
      onSelectAll(items)
    })
  }

  return {
    activeIndex,
    context,
    elementsRef,
    floatingStyles,
    getFloatingItemProps,
    getFloatingProps,
    getReferenceProps,
    getThresholdItemProps,
    handleChangeSearch,
    handleClearSearch,
    handleClose,
    handleKeyDown,
    handleOpen,
    handleSelect,
    handleSelectAll,
    handleToggleOpen,
    handleToggleShowSelected,
    isOpen,
    loadingInitial,
    loadingMore,
    options,
    refs,
    searchValue,
    setOpen,
    showSelected,
    totalCount,
  }
}
